import { ArrowLeft, BookOpen, Chrome, Library, MessageSquare, Sparkles, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export default function DocumentationPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Settings</span>
        </button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">How to use Memtrail</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Your personal knowledge assistant for the web
          </p>
        </div>

        {/* Getting Started */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            Getting Started
          </h2>
          <div className="space-y-4 text-gray-600 dark:text-gray-400">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">1. Install the Extension</h3>
              <p className="text-sm">
                Install the Memtrail Chrome extension to save content directly from any webpage.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">2. Save Your First Article</h3>
              <p className="text-sm">
                Click the Memtrail icon in your browser, then click "Save" to add the current page to your library.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">3. Organize with AI</h3>
              <p className="text-sm">
                Memtrail automatically categorizes, tags, and summarizes your content using AI.
              </p>
            </div>
          </div>
        </div>

        {/* Using the Extension */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Chrome className="w-5 h-5 text-blue-600" />
            Using the Extension
          </h2>
          <div className="space-y-4 text-gray-600 dark:text-gray-400">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Save Button</h3>
              <p className="text-sm">
                Click the "Save" button to add the current webpage to your library. Memtrail will automatically extract the content, generate a summary, and categorize it.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Chat with Page</h3>
              <p className="text-sm">
                Ask questions about the current page using the chat feature. Get instant answers without reading the entire article.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Quick Notes</h3>
              <p className="text-sm">
                Add personal notes to any saved content. Your notes are searchable and synced across devices.
              </p>
            </div>
          </div>
        </div>

        {/* Library Features */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Library className="w-5 h-5 text-blue-600" />
            Library Features
          </h2>
          <div className="space-y-4 text-gray-600 dark:text-gray-400">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Search & Filter</h3>
              <p className="text-sm">
                Find content instantly using semantic search. Filter by category, tags, or difficulty level.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Knowledge Graph</h3>
              <p className="text-sm">
                Visualize connections between your saved content. Discover related articles and topics you've explored.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Chat with Library</h3>
              <p className="text-sm">
                Ask questions across your entire library. Memtrail will search through all your saved content to find answers.
              </p>
            </div>
          </div>
        </div>

        {/* Learning Paths */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            Learning Trails
          </h2>
          <div className="space-y-4 text-gray-600 dark:text-gray-400">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Auto-Discovery</h3>
              <p className="text-sm">
                Memtrail automatically suggests learning trails based on your saved content. Topics with 3+ related articles become trail suggestions.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Create Custom Trails</h3>
              <p className="text-sm">
                Build your own learning paths by selecting specific articles. Memtrail will organize them by difficulty and reading time.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Track Progress</h3>
              <p className="text-sm">
                Mark items as completed, in progress, or skipped. See your learning streak and completion stats.
              </p>
            </div>
          </div>
        </div>

        {/* Tips & Tricks */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            Tips & Tricks
          </h2>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 dark:text-blue-400 mt-0.5">•</span>
              <span>Use keyboard shortcuts: Press 'S' to save the current page quickly</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 dark:text-blue-400 mt-0.5">•</span>
              <span>Save YouTube videos - Memtrail will extract transcripts and generate summaries</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 dark:text-blue-400 mt-0.5">•</span>
              <span>Export your library as Markdown files for use in Obsidian or other note-taking apps</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 dark:text-blue-400 mt-0.5">•</span>
              <span>Chat with specific learning trails to get contextual help on your learning journey</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
